/**
 * Quick-add-employee modal. Mounted on the PCO detail page so Joey can
 * resolve an "unknown employee" row from NeedsAttention without leaving
 * the PCO — the prior flow bounced him to /settings, made him scroll,
 * find the unknown, click Add, fill the form, save, navigate back.
 *
 * Pre-fills:
 *   - name           from the unknown-employee name (the raw text from PDF)
 *   - st_rate_cents  from project default (Davis-Bacon flat rate today)
 *   - per_diem       from project default
 *   - ot/dt          fixed defaults (1.5 / 2.0) — Joey can edit per row in
 *                    Settings if a contractor needs a different multiplier
 *
 * Heavyjob employee ID is intentionally not pre-filled. The parser knows
 * the raw name; the HJ ID is what Coffman needs Joey to enter so future
 * PDFs auto-map. The unknown-employee attention row only carries names,
 * not HJ IDs, so the field starts blank.
 */
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { ClassificationSelect } from './ClassificationSelect';
import { MoneyInput } from './MoneyInput';
import { Toggle } from './design';
import type { EmployeeWriteRequest, SettingsEmployee } from '@coffman-tm/shared';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';

export function AddEmployeeModal({
  open,
  initialHjId,
  initialName,
  defaultStRateCents,
  defaultPerDiemEligible,
  onClose,
  onAdded,
}: {
  open: boolean;
  /** HJ ID from the unknown-employee row when the parser captured one;
   *  empty string when it didn't. The user can edit either way. */
  initialHjId: string;
  initialName: string;
  defaultStRateCents: number;
  defaultPerDiemEligible: boolean;
  onClose: () => void;
  onAdded: (employee: SettingsEmployee) => void | Promise<void>;
}) {
  const { token } = useAuth();
  const [hjId, setHjId] = useState('');
  const [name, setName] = useState('');
  const [cls, setCls] = useState('');
  const [rateDollars, setRateDollars] = useState('');
  const [perDiem, setPerDiem] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setHjId(initialHjId);
    setName(initialName);
    setCls('');
    setRateDollars((defaultStRateCents / 100).toFixed(2));
    setPerDiem(defaultPerDiemEligible);
    setBusy(false);
    setErr(null);
  }, [open, initialHjId, initialName, defaultStRateCents, defaultPerDiemEligible]);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const submit = async () => {
    if (!token) return;
    if (!hjId.trim() || !name.trim() || !cls.trim()) {
      setErr('HJ ID, name, and classification are required.');
      return;
    }
    const rate = Number(rateDollars);
    if (!Number.isFinite(rate) || rate < 0) {
      setErr('Rate must be a non-negative number.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const body: EmployeeWriteRequest = {
        heavyjob_employee_id: hjId.trim(),
        name: name.trim(),
        classification: cls.trim(),
        st_rate_cents: Math.round(rate * 100),
        ot_multiplier: 1.5,
        dt_multiplier: 2.0,
        per_diem_eligible: perDiem,
        active: true,
      };
      const created = await api.settings.createEmployee(token, body);
      await onAdded(created);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to add employee');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (!busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Add employee"
    >
      <div className="new-pco-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pdf-modal-head">
          <div className="pdf-modal-title">Add to roster</div>
          <button
            type="button"
            className="pdf-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="new-pco-body">
          <div className="new-pco-row">
            <div className="new-pco-field">
              <label className="field-label">HJ ID</label>
              <input
                className="field-input mono"
                value={hjId}
                onChange={(e) => setHjId(e.target.value)}
                autoFocus
                disabled={busy}
                placeholder="from HCSS"
              />
            </div>
            <div className="new-pco-field">
              <label className="field-label">Name</label>
              <input
                className="field-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>
          <div className="new-pco-row">
            <div className="new-pco-field">
              <label className="field-label">Classification</label>
              <ClassificationSelect
                value={cls}
                onChange={setCls}
                disabled={busy}
                className="field-input"
              />
            </div>
            <div className="new-pco-field">
              <label className="field-label">ST rate (/hr)</label>
              <MoneyInput
                className="field-input mono"
                value={rateDollars}
                onChange={setRateDollars}
                disabled={busy}
              />
            </div>
          </div>
          <div className="new-pco-field">
            <span
              className="field-label"
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <Toggle
                value={perDiem}
                onChange={setPerDiem}
                disabled={busy}
                aria-label="Per-diem eligible"
              />
              Per-diem eligible
            </span>
          </div>
          {err && (
            <div style={{ color: 'var(--danger)', fontSize: 12 }}>{err}</div>
          )}
          <div className="new-pco-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-cta"
              onClick={submit}
              disabled={busy}
            >
              {busy ? 'Adding…' : 'Add to roster'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
